/* =====================================================================
 * Tiptap 확장 설정 — DocumentEditor에서 사용하는 모든 확장을 반환
 * ===================================================================== */

import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';

/* ── TableCell / TableHeader에 backgroundColor 속성 추가 (셀 배경색 기능) ── */
const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor || null,
        renderHTML: (attributes) => {
          if (!attributes.backgroundColor) return {};
          return { style: `background-color: ${attributes.backgroundColor}` };
        },
      },
    };
  },
});

const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor || null,
        renderHTML: (attributes) => {
          if (!attributes.backgroundColor) return {};
          return { style: `background-color: ${attributes.backgroundColor}` };
        },
      },
    };
  },
});
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Typography from '@tiptap/extension-typography';
import Link from '@tiptap/extension-link';
import Dropcursor from '@tiptap/extension-dropcursor';
import Details, { DetailsContent, DetailsSummary } from '@tiptap/extension-details';
import { Extension } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey, Selection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { liftListItem, sinkListItem } from '@tiptap/pm/schema-list';
import {
  ImageBlock,
  VideoBlock,
  AudioBlock,
  FileBlock,
  BookmarkBlock,
} from './extensions/mediaNodes';

const lowlight = createLowlight(common);

const LANG_MAP: Record<string, string> = {
  'Plain text': '',
  JavaScript: 'javascript',
  TypeScript: 'typescript',
  Python: 'python',
  SQL: 'sql',
  JSON: 'json',
  Bash: 'bash',
  HTML: 'xml',
  CSS: 'css',
  Java: 'java',
  C: 'c',
  'C++': 'cpp',
  Go: 'go',
  Rust: 'rust',
  Ruby: 'ruby',
  PHP: 'php',
  Swift: 'swift',
  Kotlin: 'kotlin',
  YAML: 'yaml',
  XML: 'xml',
  Markdown: 'markdown',
  Shell: 'bash',
};

const LANG_MAP_REVERSE: Record<string, string> = Object.fromEntries(
  Object.entries(LANG_MAP).map(([label, id]) => [id, label]),
);

export function lowlightIdToLabel(id?: string | null): string {
  if (!id) return 'Plain text';
  return LANG_MAP_REVERSE[id] || id;
}

export function labelToLowlightId(label?: string | null): string {
  if (!label) return '';
  return LANG_MAP[label] ?? label.toLowerCase();
}

function normalizeCodeLanguage(value?: string | null): string {
  const raw = (value || '').trim();
  if (!raw) return 'Plain text';
  const known = Object.keys(LANG_MAP);
  const matched = known.find((lang) => lang.toLowerCase() === raw.toLowerCase());
  return matched || raw;
}

export function detectCodeLanguage(code: string): string | null {
  const text = code.trim();
  if (!text) return null;
  if (/^\s*[\[{]/.test(text) && /[\]}]\s*$/.test(text)) {
    try {
      JSON.parse(text);
      return 'JSON';
    } catch {
      /* noop */
    }
  }
  if (/^\s*<!DOCTYPE\s+html/i.test(text) || /^\s*<html[\s>]/i.test(text)) return 'HTML';
  if (/^\s*<\?xml/i.test(text)) return 'XML';
  if (/^#!\s*\/bin\/(ba)?sh/.test(text) || /^\$\s+\w/.test(text)) return 'Bash';
  if (/\b(interface|type|enum)\s+\w+/.test(text) && /[{:;]/.test(text)) return 'TypeScript';
  if (/:\s*(string|number|boolean|any|void|never|unknown)\b/.test(text)) return 'TypeScript';
  if (/\b(const|let|var)\s+\w+\s*=/.test(text)) return 'JavaScript';
  if (/\bfunction\s+\w+\s*\(/.test(text)) return 'JavaScript';
  if (/\b(import|export)\s+(default\s+)?(function|class|const|let|var|\{|\*)/.test(text)) return 'JavaScript';
  if (/\bconsole\.(log|warn|error)\s*\(/.test(text)) return 'JavaScript';
  if (/\b(async|await)\b/.test(text)) return 'JavaScript';
  if (/^\s*(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|WITH)\s/im.test(text)) return 'SQL';
  if (/^(from\s+\S+\s+import|import\s+\S+)\b/.test(text) || /\bdef\s+\w+\s*\(/.test(text)) return 'Python';
  return null;
}

export interface EditorExtensionOptions {
  /** P11-5g: when false, image/video/audio/file/bookmark TipTap nodes
   * are stripped so the editor refuses to embed external media. */
  mediaEmbed?: boolean;
}

export function getEditorExtensions(options: EditorExtensionOptions = {}) {
  const { mediaEmbed = true } = options;
  const DocumentCodeBlock = CodeBlockLowlight.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        language: {
          default: 'Plain text',
          parseHTML: (element) =>
            normalizeCodeLanguage(
              element.getAttribute('data-language') || lowlightIdToLabel(element.getAttribute('language')),
            ),
          renderHTML: (attributes) => {
            const label = normalizeCodeLanguage(attributes.language) || lowlightIdToLabel(attributes.language);
            return { 'data-language': label || 'Plain text' };
          },
        },
      };
    },
  });

  /* ── 토글(Details) Summary Enter 키맵 오버라이드 ──
   * 빈 요약 + Enter → 토글 해제 (일반 paragraph로)
   * 텍스트 있는 요약 + Enter → 커서 이후 텍스트를 새 형제 토글로 분리 */
  const ToggleKeymap = Extension.create({
    name: 'toggleKeymap',
    priority: 150, // Details 확장(기본 100)보다 높아야 Enter를 먼저 처리
    addKeyboardShortcuts() {
      return {
        Enter: ({ editor }) => {
          const { state, view } = editor;
          const { schema, selection } = state;
          const { $head } = selection;

          // detailsSummary 내부에서만 동작
          if ($head.parent.type !== schema.nodes.detailsSummary) {
            return false;
          }

          const summaryNode = $head.parent;

          /* ── 빈 요약 → 토글 해제 ── */
          if (summaryNode.content.size === 0) {
            const detailsDepth = $head.depth - 1;
            const detailsNode = $head.node(detailsDepth);
            const detailsPos = $head.before(detailsDepth);

            // 컨텐츠도 비어있으면 단일 paragraph로 깔끔하게 교체
            let contentEmpty = true;
            detailsNode.forEach((child) => {
              if (child.type.name === 'detailsContent') {
                contentEmpty =
                  child.childCount === 1 &&
                  child.child(0).type.name === 'paragraph' &&
                  child.child(0).textContent === '';
              }
            });

            if (contentEmpty) {
              const { tr } = state;
              tr.replaceWith(
                detailsPos,
                detailsPos + detailsNode.nodeSize,
                schema.nodes.paragraph.create(),
              );
              tr.setSelection(Selection.near(tr.doc.resolve(detailsPos + 1)));
              tr.scrollIntoView();
              view.dispatch(tr);
              return true;
            }

            // 컨텐츠에 내용이 있으면 unsetDetails로 풀기 (내용 보존)
            return editor.commands.unsetDetails();
          }

          /* ── 텍스트 있는 요약 → 새 형제 토글 생성 ── */
          const detailsDepth = $head.depth - 1;
          const detailsNode = $head.node(detailsDepth);
          const detailsPos = $head.before(detailsDepth);
          const endPos = detailsPos + detailsNode.nodeSize;

          const cursorOffset = $head.parentOffset;
          const summaryStart = $head.start();
          const afterContent = summaryNode.content.cut(cursorOffset);

          const { tr } = state;

          // 커서 이후 텍스트 삭제
          if (afterContent.size > 0) {
            tr.delete($head.pos, summaryStart + summaryNode.content.size);
          }

          // 새 토글 생성 (커서 이후 텍스트를 새 요약에 배치)
          const newSummary = schema.nodes.detailsSummary.create(
            {},
            afterContent.size > 0 ? afterContent : undefined,
          );
          const newContent = schema.nodes.detailsContent.create(
            {},
            schema.nodes.paragraph.create(),
          );
          const newDetails = schema.nodes.details.create({}, [newSummary, newContent]);

          const mappedEndPos = tr.mapping.map(endPos);
          tr.insert(mappedEndPos, newDetails);

          // 새 요약으로 커서 이동
          const cursorPos = mappedEndPos + 2;
          tr.setSelection(Selection.near(tr.doc.resolve(cursorPos), 1));
          tr.scrollIntoView();

          view.dispatch(tr);
          return true;
        },
      };
    },
  });

  /* ── 토글 내부 플레이스홀더 (요약 "토글" + 빈 컨텐츠 안내) ──
   * Placeholder 확장과 별도로 동작하여, 토글 요약/컨텐츠에
   * 커서 유무와 무관하게 항상 표시됨 */
  const TogglePlaceholder = Extension.create({
    name: 'togglePlaceholder',
    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey('togglePlaceholder'),
          props: {
            decorations: ({ doc }) => {
              const decorations: Decoration[] = [];

              doc.descendants((node, pos) => {
                // 빈 요약 → "토글" 플레이스홀더
                if (node.type.name === 'detailsSummary' && node.textContent === '') {
                  decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, {
                      class: 'is-empty',
                      'data-placeholder': '토글',
                    }),
                  );
                  return false;
                }

                // 빈 컨텐츠 (단일 빈 paragraph) → 안내 플레이스홀더
                if (node.type.name === 'detailsContent' && node.childCount === 1) {
                  const child = node.child(0);
                  if (child.type.name === 'paragraph' && child.textContent === '') {
                    const childPos = pos + 1;
                    decorations.push(
                      Decoration.node(childPos, childPos + child.nodeSize, {
                        class: 'is-empty',
                        'data-placeholder':
                          "빈 토글입니다. '/'를 입력하여 명령어를 사용하거나, 텍스트를 입력하세요...",
                      }),
                    );
                  }
                  return true; // 중첩 토글 탐색 허용
                }

                return true;
              });

              return DecorationSet.create(doc, decorations);
            },
          },
        }),
      ];
    },
  });

  /* ── 리스트 Tab/Shift+Tab 키맵 (노션 유사 동작) ──
   * - Tab: 하위 리스트로 들여쓰기
   * - Shift+Tab: 상위 리스트로 내어쓰기
   * - 리스트 문맥에서는 브라우저 기본 포커스 이동을 차단 */
  const ListIndentKeymap = Extension.create({
    name: 'listIndentKeymap',
    priority: 200,
    addKeyboardShortcuts() {
      const runPmListCommand = (
        editor: any,
        typeName: 'listItem' | 'taskItem',
        direction: 'sink' | 'lift',
      ) => {
        const nodeType = editor.state.schema.nodes[typeName];
        if (!nodeType) return false;
        const command =
          direction === 'sink' ? sinkListItem(nodeType) : liftListItem(nodeType);
        return command(editor.state, editor.view.dispatch);
      };

      const resolveListItemType = (editor: any): 'listItem' | 'taskItem' | null => {
        const { $from } = editor.state.selection;
        for (let d = $from.depth; d >= 1; d -= 1) {
          const typeName = $from.node(d).type.name;
          if (typeName === 'table') return null;
          if (typeName === 'listItem' || typeName === 'taskItem') return typeName;
        }
        return null;
      };

      return {
        Tab: ({ editor }) => {
          const itemType = resolveListItemType(editor);
          if (!itemType) return false;

          if (itemType === 'taskItem') {
            // TaskItem은 pm command를 우선 사용 (키맵/확장 충돌 시 안정적)
            runPmListCommand(editor, 'taskItem', 'sink')
              || editor.commands.sinkListItem('taskItem');
            return true;
          }

          runPmListCommand(editor, 'listItem', 'sink')
            || editor.commands.sinkListItem('listItem');
          return true;
        },
        'Shift-Tab': ({ editor }) => {
          const itemType = resolveListItemType(editor);
          if (!itemType) return false;

          if (itemType === 'taskItem') {
            runPmListCommand(editor, 'taskItem', 'lift')
              || editor.commands.liftListItem('taskItem');
            return true;
          }

          runPmListCommand(editor, 'listItem', 'lift')
            || editor.commands.liftListItem('listItem');
          return true;
        },
      };
    },
  });

  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      codeBlock: false,
      dropcursor: false,
      link: false,
      underline: false,
    }),
    DocumentCodeBlock.configure({
      lowlight,
      defaultLanguage: null,
      HTMLAttributes: { class: 'doc-code-block' },
    }),
    Placeholder.configure({
      // 테이블 내부 노드(tr/td/th)까지 placeholder decoration이 퍼지면
      // row pseudo-element가 생겨 클릭 좌표가 밀릴 수 있어 비활성화
      includeChildren: false,
      placeholder: ({ node, editor: ed, pos }) => {
        const sel = ed.state.selection;
        if (sel instanceof NodeSelection && sel.from !== pos) return '';
        if (node.type.name === 'heading') return '제목을 입력하세요';
        if (node.type.name === 'paragraph') {
          const $pos = ed.state.doc.resolve(pos);
          if ($pos.depth === 0) {
            return "'/' 를 입력하여 명령어를 사용하거나, 텍스트를 입력하세요...";
          }
        }
        return '';
      },
    }),
    ...(mediaEmbed ? [ImageBlock, VideoBlock, AudioBlock, FileBlock, BookmarkBlock] : []),
    /* Table — draggable: true 를 extend로 주면 columnResizing의 TableView NodeView와
     * 충돌하여 colgroup/col DOM이 꼬이고 열이 밀림 (GitHub #2250, #5431 등).
     * 커스텀 드래그 핸들(HandleBtn)이 HTML5 drag를 직접 제어하므로
     * Node spec의 draggable은 필요 없음. */
    Table.configure({
      resizable: true,
      handleWidth: 5,
      cellMinWidth: 80,
      lastColumnResizable: true,
      allowTableNodeSelection: true,
    }),
    TableRow,
    CustomTableCell,
    CustomTableHeader,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Underline,
    TextStyle,
    Color,
    TaskList,
    TaskItem.configure({ nested: true }),
    Typography,
    Link.configure({ openOnClick: false, autolink: true }),
    Dropcursor.configure({ color: '#f59e0b', width: 2 }),
    Details.configure({
      persist: true,
      HTMLAttributes: { class: 'doc-details' },
    }),
    DetailsSummary,
    DetailsContent,
    ListIndentKeymap,
    ToggleKeymap,
    TogglePlaceholder,

    /* ── 문서 끝에 항상 빈 paragraph 보장 (appendTransaction 사용) ──
     * onUpdate 내 동기 dispatch 대신 같은 트랜잭션에서 처리하여
     * insertTable 등에서 이중 렌더링·레이아웃 깨짐 방지 */
    Extension.create({
      name: 'trailingParagraph',
      addProseMirrorPlugins() {
        return [
          new Plugin({
            key: new PluginKey('trailingParagraph'),
            appendTransaction(_transactions, _oldState, newState) {
              // 테이블 내부 선택 상태에서는 trailing 삽입을 건너뛴다.
              // tableEditing/columnResizing appendTransaction과의 초기 타이밍 충돌을 방지.
              const { $from } = newState.selection;
              for (let d = $from.depth; d >= 1; d -= 1) {
                if ($from.node(d).type.name === 'table') {
                  return null;
                }
              }

              const lastNode = newState.doc.lastChild;
              if (
                !lastNode ||
                lastNode.type.name !== 'paragraph' ||
                lastNode.textContent.length > 0
              ) {
                return newState.tr
                  .insert(
                    newState.doc.content.size,
                    newState.schema.nodes.paragraph.create(),
                  )
                  .setMeta('addToHistory', false);
              }
              return null;
            },
          }),
        ];
      },
    }),
  ];
}

